import invariant from "invariant";
import { useCallback } from "react";

import { useToast } from "../../components";
import useExtensionFeatureEnabled from "../../hooks/useBrowserExtensionFeatureEnabled";
import { openFeedbackForm } from "../../utils/extension";
import { CopyNotesButtonsProps } from "../components/CallNotes/CopyNotesButtons";
import { Ats, Call } from "../graphql";

type Autofillable = Pick<
  Call,
  | "greenhouseScorecardLinkWithId"
  | "leverInterviewLinkWithId"
  | "ashbyFeedbackLinkWithId"
>;

export const getAts = (recording: Autofillable): Ats | undefined =>
  recording.greenhouseScorecardLinkWithId
    ? Ats.Greenhouse
    : recording.leverInterviewLinkWithId
    ? Ats.Lever
    : recording.ashbyFeedbackLinkWithId
    ? Ats.Ashby
    : undefined;

const getAtsLink = ({
  greenhouseScorecardLinkWithId,
  leverInterviewLinkWithId,
  ashbyFeedbackLinkWithId,
}: Autofillable): string | null =>
  greenhouseScorecardLinkWithId ||
  leverInterviewLinkWithId ||
  ashbyFeedbackLinkWithId ||
  null;

const useAutofill = (
  recording: Autofillable
): Pick<CopyNotesButtonsProps, "onAutofill" | "autofillEnabled"> => {
  const toast = useToast();

  const pasteScorecardEnabled = useExtensionFeatureEnabled("paste_scorecard");

  const url = getAtsLink(recording);

  const autofillEnabled = pasteScorecardEnabled && !!url;

  const onAutofill = useCallback(() => {
    invariant(url, "useAutofill missing ATS link");

    const onError = (): void => {
      toast({
        title: "There was a problem",
        description: "Please try again later",
        status: "error",
      });
    };

    if (typeof zoomSdk !== "undefined") {
      zoomSdk.openUrl({ url });
    } else {
      openFeedbackForm(url, true).catch(onError);
    }
  }, [url]);

  return { onAutofill, autofillEnabled };
};

export default useAutofill;
